import axios, { AxiosInstance } from "axios";

// 创建专用的 axios 实例用于认证
const authAxios = axios.create({
  baseURL: "/api/admin",
  headers: {
    "Content-Type": "application/json",
  },
});

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  phone?: string;
  last_sign_in_at?: string;
  sign_in_count?: number;
  permissions: {
    can_manage_users: boolean;
    can_manage_content: boolean;
    can_manage_operations: boolean;
    can_manage_content_release: boolean;
    can_publish_content_release: boolean;
    can_rollback_content_release: boolean;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: AdminUser & {
    access_token: string;
    token_type: string;
    expires_at: string;
  };
}

export interface MfaRequiredResponse {
  success: boolean;
  message: string;
  data: {
    requires_mfa: true;
    mfa_method: string;
    admin: {
      id: number;
      email: string;
      name: string;
      role: string;
    };
  };
}

export interface MfaSetupRequiredResponse {
  success: boolean;
  message: string;
  data: {
    requires_mfa_setup: true;
    setup_token: string;
    admin: {
      id: number;
      email: string;
      name?: string;
      role: string;
    };
  };
}

export interface MfaCredentials {
  admin_id: number;
  mfa_code: string;
}

export interface MfaSetupResponse {
  success: boolean;
  message: string;
  data: {
    provisioning_uri: string;
    backup_codes: string[];
    secret: string;
  };
}

export interface MfaStatusResponse {
  success: boolean;
  message: string;
  data: {
    mfa_enabled: boolean;
    mfa_configured: boolean;
    mfa_enrolled_at: string | null;
    backup_codes_count: number;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
  error?: string;
}

class AdminAuthService {
  private accessToken: string | null = null;

  constructor() {
    // 从 localStorage 恢复 token
    this.accessToken = localStorage.getItem("admin_access_token");
    if (this.accessToken) {
      this.setAuthHeader(this.accessToken);
    }
  }

  private setAuthHeader(token: string) {
    authAxios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  private clearAuthHeader() {
    delete authAxios.defaults.headers.common["Authorization"];
  }

  login = async (
    credentials: LoginCredentials,
  ): Promise<
    AdminUser | MfaRequiredResponse["data"] | MfaSetupRequiredResponse["data"]
  > => {
    try {
      const response = await authAxios.post<
        LoginResponse | MfaRequiredResponse | MfaSetupRequiredResponse
      >("/auth/login", credentials);

      if (response.data.success && response.data.data) {
        const data = response.data.data as any;

        // 检查是否需要 MFA 验证
        if (data.requires_mfa) {
          return {
            requires_mfa: true,
            mfa_method: data.mfa_method,
            admin: data.admin,
          };
        }

        // 检查是否需要设置 MFA
        if (data.requires_mfa_setup) {
          return {
            requires_mfa_setup: true,
            setup_token: data.setup_token,
            admin: data.admin,
          };
        }

        // 正常登录成功
        const { access_token, ...adminData } = data;

        // 保存 token
        this.accessToken = access_token;
        localStorage.setItem("admin_access_token", access_token);
        this.setAuthHeader(access_token);

        return adminData;
      } else {
        throw new Error(response.data.message || "登录失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "登录失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  getCurrentAdmin = async (): Promise<AdminUser> => {
    try {
      const response = await authAxios.get<ApiResponse<AdminUser>>("/auth");

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error("获取管理员信息失败");
      }
    } catch (error) {
      // 如果是认证错误，只清理本地状态，不调用 logout（避免再次请求后端）
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.clearLocalAuth();
      }
      throw error;
    }
  };

  // 仅清理本地认证状态，不请求后端
  private clearLocalAuth = (): void => {
    this.accessToken = null;
    localStorage.removeItem("admin_access_token");
    this.clearAuthHeader();
  };

  logout = async (): Promise<void> => {
    try {
      // 尝试调用后端登出接口
      if (this.accessToken) {
        await authAxios.delete("/auth");
      }
    } catch (error) {
      // 忽略登出接口错误，继续清理本地状态
      console.warn("登出接口调用失败:", error);
    } finally {
      // 清理本地状态
      this.clearLocalAuth();
    }
  };

  refreshToken = async (): Promise<string> => {
    try {
      const response =
        await authAxios.post<
          ApiResponse<{ access_token: string; expires_at: string }>
        >("/auth/refresh");

      if (response.data.success && response.data.data) {
        const { access_token } = response.data.data;

        // 更新 token
        this.accessToken = access_token;
        localStorage.setItem("admin_access_token", access_token);
        this.setAuthHeader(access_token);

        return access_token;
      } else {
        throw new Error("刷新令牌失败");
      }
    } catch (error) {
      // 刷新失败，清除认证状态
      this.logout();
      throw error;
    }
  };

  // MFA 相关方法
  completeMfaLogin = async (
    credentials: MfaCredentials,
  ): Promise<AdminUser> => {
    try {
      const response = await authAxios.post<LoginResponse>(
        "/auth/complete_login",
        credentials,
      );

      if (response.data.success && response.data.data) {
        const { access_token, ...adminData } = response.data.data;

        // 保存 token
        this.accessToken = access_token;
        localStorage.setItem("admin_access_token", access_token);
        this.setAuthHeader(access_token);

        return adminData;
      } else {
        throw new Error(response.data.message || "MFA 验证失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "MFA 验证失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  // MFA 设置相关
  setupMfa = async (setupToken?: string): Promise<MfaSetupResponse["data"]> => {
    try {
      const headers: Record<string, string> = {};
      if (setupToken) {
        headers["X-Setup-Token"] = setupToken;
      }

      const response = await authAxios.post<MfaSetupResponse>(
        "/mfa/setup",
        {},
        { headers },
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "MFA 设置失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "MFA 设置失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  enableMfa = async (code: string, setupToken?: string): Promise<void> => {
    try {
      const data: Record<string, string> = { code };
      const headers: Record<string, string> = {};

      if (setupToken) {
        headers["X-Setup-Token"] = setupToken;
      }

      const response = await authAxios.post("/mfa/enable", data, { headers });

      if (!response.data.success) {
        throw new Error(response.data.message || "MFA 启用失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "MFA 启用失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  getMfaStatus = async (): Promise<MfaStatusResponse["data"]> => {
    try {
      const response = await authAxios.get<MfaStatusResponse>("/mfa/status");

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error("获取 MFA 状态失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "获取 MFA 状态失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  disableMfa = async (): Promise<void> => {
    try {
      const response = await authAxios.delete("/mfa");

      if (!response.data.success) {
        throw new Error(response.data.message || "MFA 禁用失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "MFA 禁用失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  regenerateBackupCodes = async (): Promise<string[]> => {
    try {
      const response = await authAxios.post("/mfa/regenerate_backup_codes");

      if (response.data.success && response.data.data) {
        return response.data.data.backup_codes;
      } else {
        throw new Error(response.data.message || "重新生成备用代码失败");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response.data.message || "重新生成备用代码失败");
      }
      throw new Error("网络错误，请稍后重试");
    }
  };

  isAuthenticated = (): boolean => {
    return !!this.accessToken;
  };

  getAccessToken = (): string | null => {
    return this.accessToken;
  };
}

export const adminAuthService = new AdminAuthService();
export default adminAuthService;
